import TextareaField from 'components/Fields/TextareaField/TextareaField';
import SelectField from 'components/Fields/SelectField/SelectField';
import {
  CLAIM_DETAILS,
  CONTENT_DETAILS,
  withFullTranslationKey,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import { getFieldValid, getFieldValue } from 'components/ContentReporting/form-utils';
import { FEATURE_FLAG, hasFeatureFlag } from 'components/ContentReporting/feature-flags';

import { AssetType } from '../../../../proto/generated/spotify/catalogmanagement/entity/v1/asset';

const OPTIONS_CLAIM_COPYRIGHT_TYPE = {
  SOUND_RECORDINGS: 'sound_recordings',
  COMPOSITION: 'composition',
  IMAGE: 'image',
  TEXT: 'text',
  VIDEO: 'video',
};

/*
 * The source of truth for this are the agreements in https://docs.google.com/document/d/1myXAXhLaDJvMMaOekVJJgcXxgBFFrqrZAvI97-K38yY
 * As well as the corresponding mapper in https://ghe.spotify.net/content-reporting/cheerleader/blob/a79995d1af54e64b4dd17f0b342387c336f93318/src/main/java/com/spotify/cheerleader/leads/mappers/ClaimToIntegrityRiskAreaMapper.java#L62
 */
const ASSET_TYPE_TO_COPYRIGHT_TYPE_OPTIONS = {
  [AssetType[AssetType.AUDIO]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
  ],
  [AssetType[AssetType.EPISODE_VIDEO_PREVIEW_AUDIO]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
  ],
  [AssetType[AssetType.SUPPLEMENTARY_PDF]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
  ],
  [AssetType[AssetType.CANVAS_ASSET]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
  ],
  [AssetType[AssetType.VIDEO]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
  ],
  [AssetType[AssetType.EPISODE_VIDEO_PREVIEW_VIDEO]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
  ],
  [AssetType[AssetType.PROFILE_IMAGE]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.PLAYLIST_IMAGE]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.ARTIST_AVATAR]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.ARTIST_MERCHANDISE]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.SOUND_RECORDINGS,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.VIDEO,
  ],
  [AssetType[AssetType.IMAGE]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.ARTIST_GALLERY_IMAGE]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.ARTIST_HEADER]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
  [AssetType[AssetType.MERCH_IMAGE_GALLERY]]: [
    OPTIONS_CLAIM_COPYRIGHT_TYPE.IMAGE,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.TEXT,
    OPTIONS_CLAIM_COPYRIGHT_TYPE.COMPOSITION,
  ],
};

export const COPYRIGHT_TYPE_REQUIRED_ASSET_TYPES = Object.keys(ASSET_TYPE_TO_COPYRIGHT_TYPE_OPTIONS);

const getCopyrightTypeOptions = (assetType: AssetType) => {
  return ASSET_TYPE_TO_COPYRIGHT_TYPE_OPTIONS[assetType] || [];
};

// Note: This interface is not perfect, but a step in the right direction over completely untyped JS.
export interface CopyrightFieldsProps {
  formData: {};
  handleFieldChange: () => void;
  forceValidation: boolean;
  shouldDisplayTypeSelection: boolean;
  t: (key: string) => string;
  enabledFeatureFlags: Set<string>;
}

export const CopyrightFields = ({
  formData,
  handleFieldChange,
  forceValidation,
  shouldDisplayTypeSelection,
  t,
  enabledFeatureFlags,
}: CopyrightFieldsProps) => {
  const fieldValue = getFieldValue(formData);
  const fieldValidValue = getFieldValid(formData);
  const isFeatureFlagEnabled = hasFeatureFlag(enabledFeatureFlags);
  const copyrightInfoLabelOverride = isFeatureFlagEnabled(FEATURE_FLAG.UMG)
    ? CLAIM_DETAILS.labels.CLAIM_COPYRIGHT_INFO_UMG
    : CLAIM_DETAILS.labels.CLAIM_COPYRIGHT_INFO;

  const assetType = fieldValue(CONTENT_DETAILS.fields.COMMON_ASSET_TYPE);

  return (
    <>
      {shouldDisplayTypeSelection && (
        <SelectField
          field={{
            name: CLAIM_DETAILS.fields.CLAIM_COPYRIGHT,
            type: 'text',
            options: getCopyrightTypeOptions(assetType),
            labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
            required: true,
          }}
          hasDefaultPlaceholder
          value={fieldValue(CLAIM_DETAILS.fields.CLAIM_COPYRIGHT)}
          valid={fieldValidValue(CLAIM_DETAILS.fields.CLAIM_COPYRIGHT)}
          tPrefix={withFullTranslationKey(CLAIM_DETAILS.translationPrefixes.CLAIM_COPYRIGHT)}
          onFieldChange={handleFieldChange}
          forceValidation={forceValidation}
        />
      )}

      <TextareaField
        field={{
          name: CLAIM_DETAILS.fields.CLAIM_COPYRIGHT_INFO,
          type: 'text',
          maxlength: 20000,
          labelOverride: t(copyrightInfoLabelOverride),
          required: true,
        }}
        value={fieldValue(CLAIM_DETAILS.fields.CLAIM_COPYRIGHT_INFO)}
        onFieldChange={handleFieldChange}
      />
    </>
  );
};
